import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Course, Topic } from './appInterfaceDataSlice';
import {
  BigBangLabPageStep,
  ChildProfilePageStep,
  CommonPageStep,
  ForgetPasswordPageStep,
  ParentPortalPageStep,
  Plans,
  ResetPasswordPageStep,
  SignupPageStep,
  SubscriptionPageStep,
  ThankyouPageStep,
} from '../../view';
import type { RootState } from '../store';

// ELEMENTARY INTERFACE
export interface ChildProfile {
  child_profile_id: string;
  parent_profile_id: string;
  child_profile_data: {
    gender?: 'MALE' | 'FEMALE';
    birthday?: string;
    school?: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
    nuts?: number;
    childAge?: string;
    childLevel?: 'Introductory' | 'Intermediate';
    programId?: string;
  };
}

// Define a type for the slice state
type AllAppPageStep =
  | SignupPageStep
  | SubscriptionPageStep
  | ThankyouPageStep
  | CommonPageStep
  | ChildProfilePageStep
  | BigBangLabPageStep
  | ForgetPasswordPageStep
  | ResetPasswordPageStep
  | ParentPortalPageStep;

interface AppState {
  authentication?: AppStateAuthentication;
  userProfile?: AppUserProfile;
  userSubscription?: AppUserSubscription;
  pageStep: AllAppPageStep;
}

export type AppUserSubscription = AppUserSubscriptionRecord[];

export type AppUserSubscriptionRecord = {
  subscription_id: string;
  user_id: string;
  subscription_type: Plans;
  subscription_detail: {
    product_home: string;
    product_type: 'SUBSCRIPTION' | 'PURCHASE';
    price_id: string;
    interval: 'ONE-OFF' | 'MONTHLY' | 'ANNUALLY';
    childId: string;
    programs: Program[];
  };
  payment_id: string;
  valid: boolean;
  create_time: string;
  subscription_start_time: string;
  subscription_end_time: string | undefined;
  subscription_response: Record<string, unknown>;
  update_time: string;
  metadata: Record<string, unknown>;
};

type ProgramDetail = ProjectAlphaDetail | Record<string, unknown>;
type ProjectAlphaDetail = {
  shipmentSchedule: ProjectAlphaShipmentSchedule;
};
type ProjectAlphaShipmentSchedule = {
  dispatchDate: Array<{
    targetDispatchDate: Date;
    targetDeliveryDate: Date;
    topicIds?: Array<Topic['id']>;
  }>;
};
const enum ProgramType {
  timed = 'timed',
}

export type Program = {
  id: string;

  title: string;

  type: ProgramType | undefined;

  programDetail: ProgramDetail;

  startedAt: Date;

  endedAt: Date;

  createdAt: Date;

  updatedAt: Date;
};
export interface AppUserProfile {
  parentProfile: any;
  childProfile: {
    first: ChildProfile | undefined;
    second: ChildProfile | undefined;
    third: ChildProfile | undefined;
  };
}

export interface AppStateAuthentication {
  token: string;
  loginToken: string;
  username: string;
  userId: string;
  refUrl: string;
  sessionId: string;
  expireTime: string;
  verified: boolean;
  needResetPassword: boolean;
}

// Define the initial state using that type
const initialState: AppState = {
  pageStep: CommonPageStep.BLANK,
};

export const appStateSlice = createSlice({
  name: 'appState',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    // App Page Step Action
    setAppPageStep(state, action: PayloadAction<AllAppPageStep>): void {
      state.pageStep = action.payload;
    },

    // Use the PayloadAction type to declare the contents of `action.payload`
    updateAppAuthentication(
      state,
      action: PayloadAction<AppStateAuthentication>,
    ): void {
      state.authentication = action.payload;
    },

    updateAppUserProfile(state, action: PayloadAction<AppUserProfile>): void {
      state.userProfile = action.payload;
    },

    updateAppUserSubscription(state, action: PayloadAction<any[]>): void {
      state.userSubscription = action.payload;
    },

    setAppStateData(
      state,
      action: PayloadAction<{ dataKey: keyof AppState; data: unknown }>,
    ): void {
      // @ts-expect-error: Stupid TS checking
      state[action.payload.dataKey] = action.payload.data;
    },
  },
});

export const {
  setAppPageStep,
  updateAppAuthentication,
  updateAppUserSubscription,
  updateAppUserProfile,
  setAppStateData,
} = appStateSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectAppPageStep = (state: RootState): AllAppPageStep => {
  return state.appState.pageStep;
};

export const selectAppAuthentication = (
  state: RootState,
): AppStateAuthentication | undefined => {
  return state.appState.authentication;
};

export const selectAppUserSubscription = (
  state: RootState,
): AppUserSubscriptionRecord[] | undefined => {
  return state.appState.userSubscription;
};

export const selectAppUserProfile = (
  state: RootState,
): AppUserProfile | undefined => {
  return state.appState.userProfile;
};

export const selectAppStateData = (state: RootState): AppState => {
  return state.appState;
};

export const childLevelToGrade = (
  childLevel: ChildProfile['child_profile_data']['childLevel'],
): Course['grade'] => {
  if (childLevel) {
    if (childLevel === 'Introductory') {
      return 'g1';
    }
    if (childLevel === 'Intermediate') {
      return 'g3';
    }
  }

  return 'g1';
};

export default appStateSlice.reducer;
